import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Section, Overline, H1, Lead, H2 } from "@/components/ui-kit";
import { api } from "@/lib/api";

const SECTIONS = [
  ["situation", "Situation"],
  ["problem", "Constraints / Risks"],
  ["strategy", "Strategy"],
  ["execution", "Execution Plan"],
  ["value_creation", "Value Creation Levers"],
  ["risk_controls", "Risk Controls"],
  ["exit_strategy", "Exit Strategy"],
  ["lessons", "Lessons Learned"],
];

export default function DealDetail() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const preview = params.get("preview") === "1";
  const [cs, setCs] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    api.get(`/case-studies/${slug}${preview ? "?preview=1" : ""}`).then((r) => setCs(r.data)).catch(() => setMissing(true));
  }, [slug, preview]);

  if (missing) return (
    <Section className="py-24" dataTestId="case-not-found">
      <Overline>Case study</Overline>
      <H2 className="mt-3 text-2xl">This case study is not published.</H2>
      <Link to="/deals" className="inline-flex items-center gap-2 mt-6 text-[#C19B6C]"><ArrowLeft size={14} /> All case studies</Link>
    </Section>
  );
  if (!cs) return <Section className="py-32"><p className="text-neutral-400">Loading…</p></Section>;
  const statusLabel = { completed: "Completed", in_progress: "In progress", planned: "Planned" }[cs.project_status] || "";
  const gallery = (cs.gallery || []).filter((g) => g.url);

  const stratTag = cs.strategy_tag || (cs.metrics || []).find((m) => /strategy/i.test(m.label))?.value || cs.deal_type || "Value-Add";

  return (
    <>
      {/* HERO with overlay (property type, location, strategy tag) */}
      <section className="relative">
        <div className="absolute inset-0">
          {cs.cover_image && <img src={cs.cover_image} alt="" className="w-full h-full object-cover" loading="lazy" />}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 lg:px-12 pt-12 md:pt-24 pb-24 md:pb-36">
          {(preview || !cs.published) && <div className="mb-6 inline-block text-[10px] uppercase tracking-widest px-3 py-1 bg-[#C19B6C] text-black" data-testid="preview-banner">Draft preview: not visible to the public</div>}
          <Link to="/deals" className="inline-flex items-center gap-2 text-sm text-neutral-300 hover:text-[#C19B6C] mb-8">
            <ArrowLeft size={14} /> All case studies
          </Link>
          <div className="flex flex-wrap gap-2 mb-5">
            <span className="text-[10px] uppercase tracking-widest px-3 py-1 border border-[#C19B6C]/40 text-[#C19B6C] bg-black/40">{cs.location}</span>
            <span className="text-[10px] uppercase tracking-widest px-3 py-1 border border-white/15 text-neutral-200 bg-black/40">{stratTag}</span>
            {statusLabel && <span className="text-[10px] uppercase tracking-widest px-3 py-1 border border-white/15 text-neutral-200 bg-black/40" data-testid="case-status">{statusLabel}</span>}
          </div>
          <H1 className="max-w-4xl">{cs.title}</H1>
          <Lead className="mt-6 max-w-3xl">{cs.summary || cs.subtitle}</Lead>
        </div>
      </section>

      {/* METRICS strip */}
      {cs.metrics?.length > 0 && (
        <section className="bg-[#0A0A0A] border-y border-white/10">
          <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 py-10 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5">
            {cs.metrics.slice(0, 5).map((m, i) => (
              <div key={i} className="bg-[#0A0A0A] p-6">
                <div className="overline">{m.label}</div>
                <div className="font-display text-2xl mt-2 text-white">{m.value}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {cs.outcomes?.length > 0 && (
        <Section className="pt-12 md:pt-16" dataTestId="case-outcomes">
          <Overline>{cs.project_status === "completed" ? "Results" : "Planned outcomes and projections"}</Overline>
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5">
            {cs.outcomes.map((o, i) => (
              <div key={i} className="bg-[#0A0A0A] p-6">
                <div className="overline">{o.label}{o.kind !== "completed" ? ` (${o.kind})` : ""}</div>
                <div className="font-display text-2xl mt-2 text-white">{o.value}</div>
              </div>
            ))}
          </div>
          {cs.outcomes.some((o) => o.kind !== "completed") && <p className="text-xs text-neutral-500 mt-3">Items marked projected or planned are not completed results and are not a guarantee of performance.</p>}
        </Section>
      )}

      {/* OPERATOR-STYLE SECTIONS */}
      <Section className="py-16 md:py-24">
        <div className="max-w-3xl">
          <Overline>Overview</Overline>
          <H2 className="text-2xl mt-3">{cs.title}</H2>
          <p className="text-neutral-300 mt-5 leading-relaxed">{cs.subtitle}</p>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-14 max-w-5xl">
          {SECTIONS.filter(([key]) => cs[key]).map(([key, label]) => (
            <div key={key}>
              <H2 className="text-xl">{label}</H2>
              <p className="text-neutral-300 mt-4 leading-relaxed text-sm whitespace-pre-line">{cs[key]}</p>
            </div>
          ))}
        </div>

        {/* HIGHLIGHT: why this deal works */}
        {cs.lessons && (
          <div className="mt-16 max-w-4xl card-frame p-7 md:p-8 border-l-4 border-l-[#C19B6C]">
            <div className="overline">Operator note</div>
            <p className="text-neutral-200 mt-3 leading-relaxed">{cs.lessons}</p>
          </div>
        )}
      </Section>

      {/* GALLERY with captions */}
      {gallery.length > 0 && (
        <Section className="pb-16 md:pb-20" dataTestId="case-image-grid">
          <Overline>Property gallery</Overline>
          <div className="mt-6 md:mt-8 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {gallery.map((g, i) => (
              <figure key={i} className="border border-white/10 overflow-hidden" data-testid={`gallery-item-${i}`}>
                <a href={g.url} target="_blank" rel="noreferrer" className="block group overflow-hidden">
                  <img src={g.url} alt={g.caption || `${cs.title} photo ${i + 1}`} loading="lazy" className="w-full h-56 md:h-64 object-cover group-hover:scale-105 transition-transform duration-700" />
                </a>
                {g.caption && <figcaption className="px-4 py-3 text-xs text-neutral-400">{g.caption}</figcaption>}
              </figure>
            ))}
          </div>
        </Section>
      )}

      {/* DECISION-STAGE CTA */}
      <section className="border-t border-white/10 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 py-20 text-center">
          <H2 className="text-2xl max-w-3xl mx-auto">Have a similar property, or want to see deals like this?</H2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to={`/contact?subject=${encodeURIComponent(cs.title)}`} className="btn-ghost px-7 py-4 font-semibold" data-testid="discuss-similar">Discuss a similar project</Link>
            <Link to="/sell" className="btn-gold px-7 py-4 font-semibold">See what options might make sense for your situation</Link>
            <Link to="/buyers" className="btn-ghost px-7 py-4 font-semibold">Get access to potential off-market opportunities</Link>
          </div>
        </div>
      </section>
    </>
  );
}
